import { useEffect, useState } from "react";
import { Alert, Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material";
import { Button } from "@foodpilot/foods";
import { useTranslation } from "react-i18next";
import { useRotateApplicationSecret } from "src/api/client-api/applications/useRotateApplicationSecret";
import { useSnackbar } from "src/utils/useSnackbar";
import { SecretRevealDialog } from "./SecretRevealDialog";

type RotateSecretDialogProps = {
    open: boolean;
    applicationId: string;
    onClose: () => void;
};

export const RotateSecretDialog = ({ open, applicationId, onClose }: RotateSecretDialogProps) => {
    const { t } = useTranslation();
    const { setSnackbarProps } = useSnackbar();
    const rotateMutation = useRotateApplicationSecret();
    const [newSecret, setNewSecret] = useState<string | null>(null);

    const isRotating = rotateMutation.isLoading;

    useEffect(() => {
        if (open) {
            setNewSecret(null);
        }
    }, [open]);

    const handleConfirm = async () => {
        try {
            const rotated = await rotateMutation.mutateAsync({ id: applicationId });
            setNewSecret(rotated.secretKey ?? null);
        } catch {
            setSnackbarProps({
                message: t("applications.rotateSecretError", "Failed to regenerate the secret"),
                type: "error",
            });
        }
    };

    const handleAcknowledgeReveal = () => {
        setNewSecret(null);
        onClose();
    };

    if (newSecret) {
        return (
            <SecretRevealDialog
                open
                secretKey={newSecret}
                title={t("applications.secretRotatedTitle", "Application secret regenerated")}
                description={t(
                    "applications.secretRotatedDescription",
                    "The secret was regenerated. Save the new key now — it won't be shown again.",
                )}
                onAcknowledge={handleAcknowledgeReveal}
            />
        );
    }

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth aria-labelledby="rotate-secret-dialog-title">
            <DialogTitle id="rotate-secret-dialog-title">
                {t("applications.rotateSecretTitle", "Regenerate application secret")}
            </DialogTitle>
            <DialogContent>
                <Alert severity="warning">
                    {t(
                        "applications.rotateSecretWarning",
                        "This immediately starts signing with a new secret. The previous secret keeps working alongside it for 48 hours, so your integration won't see signature failures while you update it — after that window, only the new secret will be accepted.",
                    )}
                </Alert>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={isRotating}>
                    {t("applications.cancel", "Cancel")}
                </Button>
                <Button onClick={handleConfirm} variant="contained" color="warning" disabled={isRotating}>
                    {t("applications.rotateSecretConfirm", "Regenerate secret")}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
